import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { AppShell } from "./components/AppShell";
import { Spinner } from "./components/ui/Feedback";
import { LoginPage, RegisterPage } from "./features/auth/AuthPages";
import { DashboardPage } from "./features/dashboard/DashboardPage";
import { TransactionsPage } from "./features/transactions/TransactionsPage";
import { LocalGate } from "./local/LocalProvider";

const AccountsPage = lazy(() => import("./features/accounts/AccountsPage"));
const AccountDetailPage = lazy(() => import("./features/accounts/AccountDetailPage"));
const BudgetsPage = lazy(() => import("./features/budgets/BudgetsPage"));
const ReportsPage = lazy(() => import("./features/reports/ReportsPage"));
const FitnessPage = lazy(() => import("./features/fitness/FitnessPage"));
const CategoriesPage = lazy(() => import("./features/categories/CategoriesPage"));
const ImportsPage = lazy(() => import("./features/imports/ImportsPage"));
const ImportReviewPage = lazy(() => import("./features/imports/ImportReviewPage"));
const DuplicatesPage = lazy(() => import("./features/review/DuplicatesPage"));
const SettingsPage = lazy(() => import("./features/settings/SettingsPage"));
const SyncIssuesPage = lazy(() => import("./features/sync/SyncIssuesPage"));
const InstallPage = lazy(() => import("./features/install/InstallPage"));

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner label="Loading your books" />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  // The app needs the device database: signed in, but not yet set up on this device, waits here.
  return <LocalGate>{children}</LocalGate>;
}

function GuestOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />
        <Route path="/register" element={<GuestOnly><RegisterPage /></GuestOnly>} />
        {/* Public and for everyone: it is the page to link to from a website. */}
        <Route path="/install" element={<InstallPage />} />
        <Route element={<RequireAuth><AppShell /></RequireAuth>}>
          <Route index element={<DashboardPage />} />
          <Route path="transactions" element={<TransactionsPage />} />
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="accounts/:accountId" element={<AccountDetailPage />} />
          <Route path="budgets" element={<BudgetsPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="fitness" element={<FitnessPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="imports" element={<ImportsPage />} />
          <Route path="imports/:batchId" element={<ImportReviewPage />} />
          <Route path="review" element={<DuplicatesPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="sync-issues" element={<SyncIssuesPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function NotFound() {
  return (
    <div className="py-20 text-center">
      <h1 className="text-xl font-semibold">Page not found</h1>
      <a href="/" className="mt-2 inline-block text-accent hover:underline">Go to dashboard</a>
    </div>
  );
}
